/*
  Homeline cart behaviour (Cart handoff, Oct 2026).

  1. Swipe to remove — touch screens, on the quick cart (snippets/cart-drawer)
     and the cart page (sections/hl-cart). Rows marked [data-hlc-swipe] have a
     foreground [data-hlc-swipe-fg] over a brown Remove action. After 10px
     the gesture locks to an axis: horizontal tracks 1:1 (rubber-banding to
     the right), vertical leaves the page to scroll. On release the end point
     is projected from velocity: past 60% of the row it slides off and
     removes, past -48px it snaps open on Remove, otherwise it closes.
     Tapping an open row, or swiping another, closes it.
     Removing in the drawer clicks the row's own cart-remove-button, so
     Dawn's cart.js does the update and the re-render exactly as before.

  2. <hl-cart> — the cart page. Prices delivery from the postcode as it is
     typed, keeps the choices in localStorage (homeline_cart_opts_v1, shared
     with the product page and the delivery band), gates the one Check out
     button, and on checkout turns the choices into the same line items and
     cart attributes the previous cart wrote, then goes to /checkout.
     Quantity changes and removals go through /cart/change.js and re-render
     the section in place; other surfaces hear about it through Dawn's
     cartUpdate pub/sub.
*/
(() => {
  /*
    Pieces in a /cart.js cart: every line except the charges the cart page
    adds (delivery, rubbish removal, surcharges). Shopify's item_count counts
    those too, so a delivery line left from an earlier visit to checkout
    turned "3" into "4" on the badge. Server-side the same rule is
    snippets/hl-cart-count.liquid (products tagged "service").
  */
  window.hlCartPieces = (cart) =>
    ((cart && cart.items) || []).reduce((n, it) => {
      const service =
        it.product_type === 'Service' ||
        it.requires_shipping === false ||
        /^(delivery|rubbish-removal|delivery-surcharge)/.test(it.handle || '');
      return service ? n : n + it.quantity;
    }, 0);

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const coarse = () => window.matchMedia('(pointer: coarse)').matches;
  const SNAP = 'transform 420ms cubic-bezier(.32,.72,0,1)';

  /* ---------- 1. Swipe to remove ---------- */

  if (!window.hlcSwipeReady) {
    window.hlcSwipeReady = true;
    let open = null; // the row currently snapped open
    let g = null; // the gesture in progress
    let suppressClick = false;

    const fgOf = (row) => row.querySelector('[data-hlc-swipe-fg]');
    const setX = (row, x, animate) => {
      const fg = fgOf(row);
      if (!fg) return;
      fg.style.transition = animate && !reducedMotion() ? SNAP : 'none';
      fg.style.transform = x ? `translateX(${x}px)` : '';
      row.classList.toggle('is-open', x < 0);
    };
    const close = (row) => {
      if (!row) return;
      setX(row, 0, true);
      if (open === row) open = null;
    };
    const rubber = (x, d = 120) => (x * d * 0.55) / (d + 0.55 * Math.abs(x));

    const removeRow = (row) => {
      const page = row.closest('hl-cart');
      if (page && typeof page.removeRow === 'function') {
        page.removeRow(row);
        return;
      }
      const btn = row.querySelector('cart-remove-button button');
      if (btn) btn.click();
    };

    const flyOut = (row) => {
      const w = row.offsetWidth;
      if (reducedMotion()) {
        row.style.transition = 'opacity 200ms ease';
        row.style.opacity = '0';
      } else {
        setX(row, -w - 24, true);
      }
      setTimeout(() => removeRow(row), reducedMotion() ? 200 : 460);
    };

    document.addEventListener('pointerdown', (e) => {
      const fg = e.target.closest('[data-hlc-swipe-fg]');
      if (!fg || !coarse() || e.button !== 0) return;
      const row = fg.closest('[data-hlc-swipe]');
      if (open && open !== row) close(open);
      const m = /translateX\((-?[\d.]+)px\)/.exec(fg.style.transform || '');
      g = { row, fg, sx: e.clientX, sy: e.clientY, x0: m ? parseFloat(m[1]) : 0, x: 0, dir: null, w: row.offsetWidth, hist: [[e.clientX, performance.now()]], pid: e.pointerId };
      g.x = g.x0;
    }, { passive: true });

    document.addEventListener('pointermove', (e) => {
      if (!g || e.pointerId !== g.pid) return;
      const dx = e.clientX - g.sx;
      const dy = e.clientY - g.sy;
      if (!g.dir) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        g.dir = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (g.dir === 'x') {
          try { g.fg.setPointerCapture(g.pid); } catch (err) {}
        }
      }
      if (g.dir !== 'x') return;
      let x = g.x0 + dx;
      if (x > 0) x = rubber(x);
      g.x = x;
      g.hist.push([e.clientX, performance.now()]);
      if (g.hist.length > 5) g.hist.shift();
      setX(g.row, x, false);
    }, { passive: true });

    const end = (e) => {
      if (!g || (e && e.pointerId !== g.pid)) return;
      const cur = g;
      g = null;
      if (cur.dir !== 'x') {
        // A tap on an open row closes it rather than following the link.
        if (!cur.dir && open === cur.row) {
          suppressClick = true;
          close(cur.row);
        }
        return;
      }
      suppressClick = true;
      const a = cur.hist[0];
      const b = cur.hist[cur.hist.length - 1];
      const v = ((b[0] - a[0]) / Math.max(1, b[1] - a[1])) * 1000;
      const d = 0.99;
      const proj = cur.x + ((v / 1000) * d) / (1 - d);
      if (proj < -cur.w * 0.6) {
        open = null;
        flyOut(cur.row);
      } else if (proj < -48) {
        setX(cur.row, -96, true);
        open = cur.row;
      } else {
        close(cur.row);
      }
    };
    document.addEventListener('pointerup', end, { passive: true });
    document.addEventListener('pointercancel', end, { passive: true });

    // After a drag (or a tap that closed a row) the browser still fires a
    // click on whatever was under the finger: swallow that one.
    document.addEventListener('click', (e) => {
      if (suppressClick && e.target.closest('[data-hlc-swipe-fg]')) {
        e.preventDefault();
        e.stopPropagation();
      }
      suppressClick = false;
    }, true);

    // The Remove action revealed under an open row.
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-hlc-swipe-remove]');
      if (!btn) return;
      const row = btn.closest('[data-hlc-swipe]');
      if (row) {
        open = null;
        flyOut(row);
      }
    });
  }

  /* ---------- 2. The cart page ---------- */

  if (customElements.get('hl-cart')) return;

  const OPTS_KEY = 'homeline_cart_opts_v1';
  const money = (cents) => {
    const n = (Number(cents) || 0) / 100;
    const whole = Math.round(n) === n;
    return '$' + n.toLocaleString('en-AU', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 });
  };
  const moneyCents = (cents) =>
    '$' + ((Number(cents) || 0) / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const readOpts = () => {
    try {
      return JSON.parse(localStorage.getItem(OPTS_KEY)) || {};
    } catch (e) {
      return {};
    }
  };

  const parseRanges = (str) =>
    String(str || '')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)
      .map((t) => {
        const r = t.split('-').map((n) => parseInt(n, 10));
        if (r.length === 2 && !isNaN(r[0]) && !isNaN(r[1])) return [r[0], r[1]];
        return isNaN(r[0]) ? null : [r[0], r[0]];
      })
      .filter(Boolean);

  const headerH = () =>
    parseInt(getComputedStyle(document.documentElement).getPropertyValue('--hl-header-h'), 10) ||
    (window.matchMedia('(min-width: 990px)').matches ? 72 : 60);

  const Base =
    window.StandardEvents && window.StandardEvents.createViewEventElement
      ? window.StandardEvents.createViewEventElement(HTMLElement)
      : HTMLElement;

  class HlCart extends Base {
    connectedCallback() {
      if (super.connectedCallback) super.connectedCallback();
      if (this._ready) return;
      this._ready = true;

      const saved = readOpts();
      this.state = {
        fulfilment: saved.fulfilment === 'pickup' ? 'pickup' : 'delivery',
        postcode: /^\d{0,4}$/.test(saved.postcode || '') ? saved.postcode || '' : '',
        rubbish: !!saved.rubbish,
        agree: Object.assign({ fit: false, pickup: false, special: false, terms: false }, saved.agree || {}),
        split: saved.split === 'together' ? 'together' : 'first',
      };

      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('input', (e) => this.onInput(e));

      if (window.subscribe && window.PUB_SUB_EVENTS) {
        this._unsub = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && event.source === 'hl-cart') return;
          this.refresh();
        });
      }
      this.readData();
      this.render();
    }

    disconnectedCallback() {
      if (this._unsub) this._unsub();
    }

    $(sel) {
      return this.querySelector(sel);
    }

    readData() {
      const zonesEl = this.$('[data-hlc-zones]');
      const varEl = this.$('[data-hlc-delivery-variants]');
      this.variants = varEl ? JSON.parse(varEl.textContent || '[]') : [];
      this.zones = (zonesEl ? JSON.parse(zonesEl.textContent || '[]') : []).map((z) => {
        const v = this.variants.find((x) => x.zone === z.name);
        // Show what is charged: the zone variant's price when there is one.
        return { name: z.name, ranges: parseRanges(z.postcodes), fee: v ? v.price : Math.round((Number(z.fee) || 0) * 100), variantId: v ? v.id : null };
      });
      const d = this.dataset;
      this.subtotal = parseInt(d.subtotal, 10) || 0;
      this.hasFurniture = d.hasFurniture === 'true';
      this.hasSpecial = d.hasSpecial === 'true';
      this.isMixed = d.mixed === 'true';
      this.rubbishFee = parseInt(d.rubbishFee, 10) || 0;
      this.rubbishVariantId = d.rubbishVariantId;
    }

    persist() {
      try {
        const prev = readOpts();
        localStorage.setItem(OPTS_KEY, JSON.stringify(Object.assign(prev, this.state)));
      } catch (e) {}
    }

    zoneFor(pc) {
      if (!/^\d{4}$/.test(pc)) return null;
      const n = parseInt(pc, 10);
      // First match wins: the zone table lists Brisbane last on purpose.
      return this.zones.find((z) => z.ranges.some((r) => n >= r[0] && n <= r[1])) || null;
    }

    computed() {
      const s = this.state;
      const isPickup = this.hasFurniture && s.fulfilment === 'pickup';
      const isDelivery = this.hasFurniture && !isPickup;
      const full = /^\d{4}$/.test(s.postcode);
      const zone = isDelivery ? this.zoneFor(s.postcode) : null;
      const fee = zone ? zone.fee : 0;
      const rubbish = isDelivery && s.rubbish && this.rubbishVariantId ? this.rubbishFee : 0;
      const total = this.subtotal + fee + rubbish;

      const checks = Array.from(this.querySelectorAll('[data-hlc-check]')).filter((el) => {
        const k = el.dataset.hlcCheck;
        if (k === 'fit') return isDelivery;
        if (k === 'pickup') return isPickup;
        return true;
      });
      const left = checks.filter((el) => !s.agree[el.dataset.hlcCheck]).length;
      const needPc = isDelivery && !zone;
      const ready = !needPc && left === 0;
      return { isPickup, isDelivery, full, zone, fee, rubbish, total, checks, left, needPc, ready };
    }

    render() {
      if (this.dataset.empty === 'true') return;
      const s = this.state;
      const c = this.computed();
      this._c = c;

      // Delivery or pickup
      this.querySelectorAll('[data-hlc-mode]').forEach((b) => {
        b.setAttribute('aria-checked', String(b.dataset.hlcMode === (c.isPickup ? 'pickup' : 'delivery')));
      });
      const modeFee = this.$('[data-hlc-mode-fee]');
      if (modeFee) modeFee.textContent = c.zone ? money(c.zone.fee) : '';

      // Postcode panel
      const pc = this.$('[data-hlc-pc]');
      if (pc) {
        pc.hidden = !c.isDelivery;
        pc.dataset.state = c.zone ? 'ok' : c.full ? 'out' : 'idle';
        const input = this.$('[data-hlc-pc-input]');
        if (input && input.value !== s.postcode) input.value = s.postcode;
        if (input) input.setAttribute('aria-invalid', String(c.full && !c.zone));
        this.$('[data-hlc-pc-ok]').hidden = !c.zone;
        this.$('[data-hlc-pc-out]').hidden = !(c.full && !c.zone);
        this.$('[data-hlc-pc-help]').hidden = c.full;
        if (c.zone) {
          this.$('[data-hlc-zone]').textContent = c.zone.name;
          this.$('[data-hlc-zone-fee]').textContent = money(c.zone.fee);
        }
        this.$('[data-hlc-pc-val]').textContent = s.postcode;
      }

      // Mixed order: one delivery or two
      const split = this.$('[data-hlc-split]');
      if (split) {
        split.hidden = !c.isDelivery;
        this.querySelectorAll('[data-hlc-split-opt]').forEach((b) => {
          b.setAttribute('aria-checked', String(b.dataset.hlcSplitOpt === s.split));
        });
      }
      const pickup = this.$('[data-hlc-pickup]');
      if (pickup) pickup.hidden = !c.isPickup;

      // Extras
      const extras = this.$('[data-hlc-extras]');
      if (extras) extras.hidden = !c.isDelivery;
      const rub = this.$('[data-hlc-rubbish]');
      if (rub) rub.setAttribute('aria-checked', String(!!s.rubbish));

      // Summary
      const delLabel = this.$('[data-hlc-del-label]');
      if (delLabel) {
        const goPc = this.$('[data-hlc-goto-pc]');
        const delVal = this.$('[data-hlc-del-val]');
        if (c.isPickup) {
          delLabel.textContent = 'Pickup, Redland Bay';
          delVal.textContent = 'Free';
        } else {
          delLabel.textContent = c.zone ? 'Delivery & assembly, ' + c.zone.name : 'Delivery & assembly';
          delVal.textContent = c.zone ? money(c.zone.fee) : '';
        }
        goPc.hidden = !c.needPc;
        delVal.hidden = c.needPc;
      }
      const rubRow = this.$('[data-hlc-rub-row]');
      if (rubRow) rubRow.hidden = !c.rubbish;
      const total = this.$('[data-hlc-total]');
      if (total) total.textContent = money(c.total);
      const ap = this.$('[data-hlc-afterpay]');
      if (ap) {
        ap.hidden = c.total > (parseInt(ap.dataset.limit, 10) || 400000) || c.total <= 0;
        const amt = ap.querySelector('[data-hlc-afterpay-amount]');
        if (amt) amt.textContent = moneyCents(Math.floor(c.total / 4));
      }

      // Agreements
      this.querySelectorAll('[data-hlc-check]').forEach((el) => {
        const k = el.dataset.hlcCheck;
        if (k === 'fit') el.hidden = !c.isDelivery;
        if (k === 'pickup') el.hidden = !c.isPickup;
        const box = el.querySelector('[role="checkbox"]');
        if (box) box.setAttribute('aria-checked', String(!!s.agree[k]));
      });

      // Check out
      const btn = this.$('[data-hlc-checkout]');
      btn.classList.toggle('is-ready', c.ready);
      btn.setAttribute('aria-disabled', String(!c.ready));
      const help = this.$('[data-hlc-help]');
      help.classList.toggle('is-ready', c.ready);
      if (c.ready) {
        help.textContent = 'Next you’ll add your address and payment. Nothing is charged until then.';
      } else {
        const missing = [];
        if (c.needPc) missing.push(c.full ? 'choose pickup or email us about delivery' : 'add your postcode');
        if (c.left) {
          const n = c.checks.length;
          missing.push(
            'tick ' +
              (c.left === n ? (c.left === 1 ? 'the box' : 'all ' + c.left + ' boxes') : c.left + ' more box' + (c.left > 1 ? 'es' : ''))
          );
        }
        help.textContent = 'To continue, ' + missing.join(' and ') + '.';
      }
    }

    set(patch) {
      Object.assign(this.state, patch);
      this.persist();
      this.render();
    }

    onInput(e) {
      if (!e.target.matches('[data-hlc-pc-input]')) return;
      const v = e.target.value.replace(/\D/g, '').slice(0, 4);
      e.target.value = v;
      this.set({ postcode: v });
    }

    onClick(e) {
      const t = e.target;
      const mode = t.closest('[data-hlc-mode]');
      if (mode) return this.set({ fulfilment: mode.dataset.hlcMode });
      const splitOpt = t.closest('[data-hlc-split-opt]');
      if (splitOpt) return this.set({ split: splitOpt.dataset.hlcSplitOpt });
      if (t.closest('[data-hlc-rubbish]')) return this.set({ rubbish: !this.state.rubbish });

      // An agreement toggles from its box or its words, but not from a link in them.
      const check = t.closest('[data-hlc-check]');
      if (check && !t.closest('a')) {
        const k = check.dataset.hlcCheck;
        this.state.agree[k] = !this.state.agree[k];
        this.persist();
        this.render();
        return;
      }

      if (t.closest('[data-hlc-goto-pc]')) return this.goTo('pc');
      if (t.closest('[data-hlc-checkout]')) return this.checkout();

      const qty = t.closest('[data-hlc-qty]');
      if (qty) {
        const row = qty.closest('[data-hlc-line]');
        const next = Math.max(1, (parseInt(row.dataset.qty, 10) || 1) + parseInt(qty.dataset.hlcQty, 10));
        if (next !== parseInt(row.dataset.qty, 10)) this.change(row, next);
        return;
      }
      if (t.closest('[data-hlc-remove]')) this.removeRow(t.closest('[data-hlc-line]'));
    }

    goTo(need) {
      const el = this.$(`[data-need="${need}"]`);
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY - headerH() - 24;
      window.scrollTo({ top, behavior: reducedMotion() ? 'auto' : 'smooth' });
      if (need === 'pc') {
        const input = this.$('[data-hlc-pc-input]');
        if (input) setTimeout(() => input.focus({ preventScroll: true }), reducedMotion() ? 0 : 400);
      }
    }

    // Swipe and the Remove button both land here. The row collapses while the
    // cart updates.
    removeRow(row) {
      if (!row || row.dataset.removing) return;
      row.dataset.removing = 'true';
      row.style.maxHeight = row.offsetHeight + 'px';
      requestAnimationFrame(() => {
        row.classList.add('is-leaving');
        row.style.maxHeight = '0px';
      });
      this.change(row, 0);
    }

    lineEvent(action, row, quantity) {
      const { CartLinesUpdateEvent } = window.StandardEvents || {};
      if (!CartLinesUpdateEvent || !row.dataset.variantId || !row.dataset.key) return null;
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({ action, context: 'cart', lines: [{ id: row.dataset.key, quantity }], promise: deferred.promise })
      );
      return deferred;
    }

    async change(row, quantity) {
      if (this._busy) return;
      this._busy = true;
      this.classList.add('is-busy');
      const deferred = this.lineEvent(quantity === 0 ? 'remove' : 'update', row, quantity);
      const err = row.querySelector('[data-hlc-line-error]');
      // Homeline: a removal can be undone (assets/hl-cart-undo.js).
      const hlRemoval = quantity === 0 && window.hlCartRemoving ? window.hlCartRemoving({ id: row.dataset.key }) : null;
      try {
        if (hlRemoval) await hlRemoval.ready;
        const res = await fetch(((window.routes && window.routes.cart_change_url) || '/cart/change') + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: row.dataset.key, quantity, sections: [this.dataset.sectionId, 'cart-icon-bubble'], sections_url: window.location.pathname }),
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
        if (hlRemoval) hlRemoval.settle(data);
        if (deferred) {
          const { CartLinesUpdateEvent } = window.StandardEvents || {};
          deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data) });
        }
        this.paint(data.sections || {});
        if (window.publish && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'hl-cart', cartData: data });
        }
      } catch (e) {
        if (deferred) deferred.reject(e);
        delete row.dataset.removing;
        row.classList.remove('is-leaving');
        row.style.maxHeight = '';
        const fg = row.querySelector('[data-hlc-swipe-fg]');
        if (fg) fg.style.transform = '';
        row.style.opacity = '';
        if (err) {
          err.textContent = e.message;
          err.hidden = false;
        }
      } finally {
        this._busy = false;
        this.classList.remove('is-busy');
      }
    }

    // Swap in freshly rendered section HTML, keeping the shopper's choices.
    paint(sections) {
      const html = sections[this.dataset.sectionId];
      if (html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.querySelector('hl-cart');
        if (next) {
          Array.from(next.attributes).forEach((a) => {
            if (a.name.startsWith('data-')) this.setAttribute(a.name, a.value);
          });
          this.innerHTML = next.innerHTML;
          this.readData();
          this.render();
        }
      }
      const bubble = sections['cart-icon-bubble'];
      const bubbleEl = document.getElementById('cart-icon-bubble');
      if (bubble && bubbleEl) {
        const doc = new DOMParser().parseFromString(bubble, 'text/html');
        const inner = doc.querySelector('.shopify-section');
        if (inner) bubbleEl.innerHTML = inner.innerHTML;
      }
    }

    // Another surface (the drawer) changed the cart: re-render from the server.
    async refresh() {
      try {
        const res = await fetch(`${window.location.pathname}?sections=${encodeURIComponent(this.dataset.sectionId)}`);
        this.paint(await res.json());
      } catch (e) {}
    }

    async checkout() {
      const c = this._c || this.computed();
      if (!c.ready) {
        this.goTo(c.needPc ? 'pc' : 'ticks');
        return;
      }
      if (this._checkingOut) return;
      this._checkingOut = true;
      const btn = this.$('[data-hlc-checkout]');
      const label = btn.textContent;
      btn.textContent = 'Taking you to checkout…';
      btn.setAttribute('aria-busy', 'true');

      const s = this.state;
      // Clear any delivery line from an earlier visit before adding this one.
      const updates = {};
      this.variants.forEach((v) => {
        updates[v.id] = 0;
      });
      if (c.isDelivery && c.zone && c.zone.variantId) updates[c.zone.variantId] = 1;
      if (this.rubbishVariantId) updates[this.rubbishVariantId] = c.rubbish > 0 ? 1 : 0;

      const yn = (v) => (v ? 'Yes' : 'No');
      try {
        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            updates,
            attributes: {
              // Same names and values the previous cart wrote: order handling reads them.
              Fulfilment: c.isPickup ? 'Pickup · Redland Bay' : 'Delivery & assembly',
              'Delivery preference': !this.isMixed
                ? 'N/A'
                : s.split === 'together'
                ? 'Everything together when the made-to-order piece is ready'
                : 'In-stock pieces first, made-to-order piece to follow',
              'Delivery postcode': c.isPickup ? '' : s.postcode || '',
              'Delivery zone': c.isDelivery && c.zone ? c.zone.name : '',
              'Payment preference': 'Pay in full',
              'Agree Fit Guide': c.isDelivery ? yn(s.agree.fit) : 'N/A',
              'Agree pickup terms': c.isPickup ? yn(s.agree.pickup) : 'N/A',
              'Agree Special Order Terms': this.hasSpecial ? yn(s.agree.special) : 'N/A',
              'Agree Terms & Privacy': yn(s.agree.terms),
            },
          }),
        });
        if (!res.ok) throw new Error('update failed');
        window.location.href = this.dataset.checkoutUrl || '/checkout';
      } catch (e) {
        this._checkingOut = false;
        btn.textContent = label;
        btn.removeAttribute('aria-busy');
        const help = this.$('[data-hlc-help]');
        if (help) help.textContent = 'We couldn’t reach checkout. Please try again.';
      }
    }
  }

  customElements.define('hl-cart', HlCart);
})();
